import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDownTrayIcon, CheckIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { memoryAPI } from '../../services/api'

const MEMORY_CATEGORIES = ['restriction', 'preference', 'schedule', 'equipment_location', 'decision', 'correction']

const languageOf = (lng) => (lng?.toLowerCase().startsWith('pt') ? 'pt-BR' : (lng || 'en').split('-')[0])

function MemoryRow({ memory, onSave, onDelete, onConfirm, busy, showConfirm }) {
  const { t, i18n } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(memory.text)
  const [category, setCategory] = useState(memory.category)
  const date = new Date(memory.created_at).toLocaleDateString(i18n.language)

  if (editing) {
    return (
      <li className="py-3 space-y-2" data-testid={`memory-${memory.id}`}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={2}
          className="form-input w-full" aria-label={t('memory.text')} data-testid={`memory-edit-text-${memory.id}`} />
        <div className="flex flex-wrap items-center gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="form-input w-auto"
            aria-label={t('memory.category')}>
            {MEMORY_CATEGORIES.map((c) => <option key={c} value={c}>{t(`memory.categories.${c}`)}</option>)}
          </select>
          <button type="button" className="btn-primary text-sm" disabled={busy || !text.trim()}
            data-testid={`memory-save-${memory.id}`}
            onClick={() => onSave({ text: text.trim(), category }).then(() => setEditing(false))}>
            {t('memory.save')}
          </button>
          <button type="button" className="btn-outline text-sm" onClick={() => setEditing(false)}>{t('memory.cancel')}</button>
        </div>
      </li>
    )
  }

  return (
    <li className="py-3 flex items-start justify-between gap-3" data-testid={`memory-${memory.id}`}>
      <div className="min-w-0">
        <p className="text-gray-900">{memory.text}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {memory.source === 'chat' ? t('memory.fromChat') : t('memory.enteredByYou')} · {date}
          {memory.review_reason ? ` · ${memory.review_reason}` : ''}
        </p>
        {memory.contradicted_by && (
          <p className="text-xs text-amber-800 mt-0.5" data-testid={`memory-contradicted-${memory.id}`}>
            {memory.contradicted_by.kind === 'supplement'
              ? t('memory.contradictedBySupplement', { item: memory.contradicted_by.item })
              : t('memory.contradictedByLog', { date: new Date(memory.contradicted_by.date).toLocaleDateString(i18n.language), item: memory.contradicted_by.item })}
          </p>
        )}
        {memory.condition_met && (
          <p className="text-xs text-amber-800 mt-0.5" data-testid={`memory-condition-${memory.id}`}>
            {t('memory.conditionMet', { marker: memory.condition_met.marker, date: new Date(memory.condition_met.date).toLocaleDateString(i18n.language) })}
          </p>
        )}
      </div>
      <div className="flex shrink-0 gap-1">
        {showConfirm && (
          <button type="button" className="btn-outline text-sm flex items-center gap-1" disabled={busy}
            data-testid={`memory-keep-${memory.id}`} onClick={onConfirm}>
            <CheckIcon className="h-4 w-4" aria-hidden="true" />{t('memory.keep')}
          </button>
        )}
        <button type="button" className="p-2 text-gray-600" aria-label={t('memory.edit')}
          data-testid={`memory-edit-${memory.id}`} onClick={() => setEditing(true)}>
          <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <button type="button" className="p-2 text-red-700" aria-label={t('memory.delete')} disabled={busy}
          data-testid={`memory-delete-${memory.id}`}
          onClick={() => { if (window.confirm(t('memory.confirmDelete'))) onDelete() }}>
          <TrashIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </li>
  )
}

/**
 * VH-235: Profile → Memory. Everything the app remembers about the user, by category, with where
 * each entry came from. Add, edit, delete, "still true?", export. Nothing gets in here without the
 * user: they type it, or (stage 2) they save what the chat proposed.
 */
export function MemoryCard() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const language = languageOf(i18n.language)
  const [search, setSearch] = useState('')
  const [text, setText] = useState('')
  const [category, setCategory] = useState('restriction')
  const [error, setError] = useState(null)

  const memories = useQuery({
    queryKey: ['memories', language],
    queryFn: () => memoryAPI.list({ language, translate: language !== 'en' }).then((r) => r.data.memories),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['memories'] })
  const fail = () => setError(t('memory.failed'))

  const create = useMutation({
    mutationFn: () => memoryAPI.create({ text: text.trim(), category, language, source: 'manual' }),
    onSuccess: () => { setText(''); setError(null); refresh() },
    onError: fail,
  })
  const update = useMutation({
    mutationFn: ({ id, data }) => memoryAPI.update(id, { ...data, language }),
    onSuccess: refresh, onError: fail,
  })
  const remove = useMutation({ mutationFn: (id) => memoryAPI.remove(id), onSuccess: refresh, onError: fail })
  const confirm = useMutation({ mutationFn: (id) => memoryAPI.confirm(id), onSuccess: refresh, onError: fail })
  const busy = create.isPending || update.isPending || remove.isPending || confirm.isPending

  const exportFile = async () => {
    try {
      const response = await memoryAPI.exportFile()
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = 'vitahub-memory.md'
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      fail()
    }
  }

  const list = useMemo(() => memories.data ?? [], [memories.data])
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return needle ? list.filter((m) => `${m.text} ${m.text_en}`.toLowerCase().includes(needle)) : list
  }, [list, search])
  const review = filtered.filter((m) => m.still_true)
  const rest = filtered.filter((m) => !m.still_true)

  const row = (m, showConfirm) => (
    <MemoryRow key={m.id} memory={m} busy={busy} showConfirm={showConfirm}
      onSave={(data) => update.mutateAsync({ id: m.id, data })}
      onDelete={() => remove.mutate(m.id)} onConfirm={() => confirm.mutate(m.id)} />
  )

  return (
    <div className="space-y-6" data-testid="memory-card">
      <div className="card">
        <div className="card-header flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-medium text-gray-900">{t('memory.title')}</h3>
          {list.length > 0 && (
            <button type="button" className="btn-outline text-sm flex items-center gap-1" onClick={exportFile}
              data-testid="memory-export">
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />{t('memory.export')}
            </button>
          )}
        </div>
        <div className="card-body space-y-3">
          <p className="text-sm text-gray-600">{t('memory.intro')}</p>
          <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) create.mutate() }}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={2}
              placeholder={t('memory.placeholder')} aria-label={t('memory.text')} className="form-input w-full"
              data-testid="memory-new-text" />
            <div className="flex flex-wrap items-center gap-2">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="form-input w-auto"
                aria-label={t('memory.category')} data-testid="memory-new-category">
                {MEMORY_CATEGORIES.map((c) => <option key={c} value={c}>{t(`memory.categories.${c}`)}</option>)}
              </select>
              <button type="submit" className="btn-primary text-sm" disabled={busy || !text.trim()} data-testid="memory-add">
                {t('memory.add')}
              </button>
            </div>
          </form>
          {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
        </div>
      </div>

      {memories.isLoading && <p className="text-sm text-gray-600">{t('memory.loading')}</p>}

      {!memories.isLoading && list.length === 0 && (
        <div className="card"><div className="card-body text-sm text-gray-600 space-y-1" data-testid="memory-empty">
          <p>{t('memory.emptyTitle')}</p>
          <p className="text-gray-500">{t('memory.emptyExample')}</p>
        </div></div>
      )}

      {list.length > 0 && (
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('memory.search')}
          aria-label={t('memory.search')} className="form-input w-full" data-testid="memory-search" />
      )}

      {review.length > 0 && (
        <div className="card border-amber-300" data-testid="memory-review">
          <div className="card-header"><h4 className="font-medium text-gray-900">{t('memory.stillTrue')}</h4>
            <p className="text-xs text-gray-600">{t('memory.stillTrueHint')}</p></div>
          <ul className="px-4 sm:px-6 py-1 divide-y divide-gray-100">{review.map((m) => row(m, true))}</ul>
        </div>
      )}

      {MEMORY_CATEGORIES.map((c) => {
        const rows = rest.filter((m) => m.category === c)
        if (!rows.length) return null
        return (
          <div className="card" key={c} data-testid={`memory-group-${c}`}>
            <div className="card-header"><h4 className="font-medium text-gray-900">{t(`memory.categories.${c}`)}</h4></div>
            <ul className="px-4 sm:px-6 py-1 divide-y divide-gray-100">{rows.map((m) => row(m, false))}</ul>
          </div>
        )
      })}
    </div>
  )
}

export default MemoryCard
